import { Plus } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { ContentBlockWarningsItem } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { FormField, FormItem, FormMessage } from '@nucleus-commerce/ui/components/form';

import { emptyBannerItem } from '../../api/mappers';
import { MAX_ITEMS, type BannerFormInput } from '../../schemas/block-form-schema';
import { SlideCard } from '../slides/slide-card';

import { useActiveSlide } from './active-slide';

export interface BannerEditorProps {
  storeId: string | null;
  warnings?: readonly ContentBlockWarningsItem[];
}

/**
 * The banner form body: an ordered list of collapsible slides. One slide is expanded at a
 * time; adding a slide expands it, moving one keeps the expanded slide in view. The expanded
 * slide is the preview's current slide (see ActiveSlideContext).
 */
export function BannerEditor({ storeId, warnings = [] }: BannerEditorProps) {
  const { t } = useTranslation('content');
  const form = useFormContext<BannerFormInput>();
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'items',
  });
  const { active: expanded, setActive: setExpanded } = useActiveSlide();

  const add = () => {
    append(emptyBannerItem());
    setExpanded(fields.length);
  };
  const moveSlide = (from: number, to: number) => {
    move(from, to);
    setExpanded((current) => (current === from ? to : current === to ? from : current));
  };
  const removeSlide = (index: number) => {
    remove(index);
    setExpanded((current) =>
      current === null || current === index ? null : current > index ? current - 1 : current,
    );
  };

  return (
    <FormSection title={t('sections.slides')} description={t('sections.slidesDescription')}>
      <FormField
        control={form.control}
        name="items"
        render={() => (
          <FormItem>
            <FormMessage />
          </FormItem>
        )}
      />
      <ol className="flex flex-col gap-3" aria-label={t('sections.slides')}>
        {fields.map((row, index) => (
          <SlideCard
            key={row.id}
            index={index}
            count={fields.length}
            storeId={storeId}
            expanded={expanded === index}
            warnings={warnings}
            onToggle={() => setExpanded((current) => (current === index ? null : index))}
            onMoveUp={() => moveSlide(index, index - 1)}
            onMoveDown={() => moveSlide(index, index + 1)}
            onRemove={() => removeSlide(index)}
          />
        ))}
      </ol>
      {fields.length < MAX_ITEMS ? (
        <Button type="button" variant="outline" className="w-full border-dashed" onClick={add}>
          <Plus aria-hidden />
          {t('slide.add')}
        </Button>
      ) : null}
    </FormSection>
  );
}
